// MACH-REF-I18N-1 (spec ToDo/Machinery/MACH_REF_I18N_SPEC.md §4, P4) — labels for the Machinery
// reference tables (machine type, operation type, axis configuration, automation level, plus the
// generic "Other" row of control-system brands and taper types).
//
// Every row carries an immutable kebab-case `code` next to its English `name`. The UI translates by
// code through `common:machineRef.{kind}.{code}.label`. A row with no code, or a code with no key in
// the current language, renders its English `name` — that is the safe state while the backend that
// sends codes is not yet live, and for any row an agent adds before its keys ship (§3.4).
//
// `name` stays the canonical identifier: option VALUES, slice filters and `MachineTypes` constants
// keep using it. Only the visible text changes.
import i18next from "i18next";

/** Translate function shape shared by the hook `translate` and `i18next.t`. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;
export const defaultTranslate: Translate = (key, options) => i18next.t(key, options) as string;

export type MachineRefKind = "type" | "operation" | "axis" | "automation" | "brand" | "taper";

/** Any reference row, nested (`machine.operationType`) or rebuilt from flat `*Name`/`*Code` fields. */
export interface MachineRef {
  name?: string | null;
  code?: string | null;
}

const MISSING = "\u0000machineRef:missing";

/**
 * One `common:machineRef.*` string in the CURRENT language only. i18next's fallback to English is
 * switched off for this lookup, so a composed name can detect a missing piece instead of silently
 * borrowing an English fragment. Returns null when the key is absent (or empty).
 */
export const lookupMachineRef = (
  path: string,
  translate: Translate = defaultTranslate,
  vars?: Record<string, unknown>
): string | null => {
  const value = translate(`common:machineRef.${path}`, { ...vars, defaultValue: MISSING, fallbackLng: [] });
  return typeof value === "string" && value !== "" && value !== MISSING ? value : null;
};

/**
 * Name fallback for MACHINE TYPES only (P5 kept it so nothing regresses before the backend sends
 * codes). The type set is closed and its English names are already load-bearing constants
 * (customer/machinery/models/models.ts `MachineTypes`, slice filters). Values = DB-2 codes.
 * The other tables are open sets: no name map, a row without a code shows its English name.
 */
const MACHINE_TYPE_CODE_BY_NAME: Record<string, string> = {
  "CNC Machine": "cnc-machine",
  Compressor: "compressor",
  "Electro device below 10kW": "electro-device-below-10kw",
  "Electro device over 10kW": "electro-device-over-10kw",
  "Gas device": "gas-device",
  "Hydraulic system": "hydraulic-system",
  Other: "other",
  "Production line": "production-line",
  "Water and oil pump": "water-oil-pump",
};

/** The code a row translates by: its own `code`; for machine types, else the code its name maps to. */
export const resolveRefCode = (kind: MachineRefKind, ref?: MachineRef | null): string | null => {
  if (!ref) return null;
  if (ref.code) return ref.code;
  return kind === "type" && ref.name ? MACHINE_TYPE_CODE_BY_NAME[ref.name] || null : null;
};

/** Localised label of a reference row; English `name` when there is no code or no key. */
export const getReferenceLabel = (
  kind: MachineRefKind,
  ref: MachineRef | null | undefined,
  translate: Translate = defaultTranslate
): string => {
  if (!ref) return "";
  const code = resolveRefCode(kind, ref);
  const label = code ? lookupMachineRef(`${kind}.${code}.label`, translate) : null;
  return label || ref.name || "";
};

/** The generic "Other" row of any table (sorts last, skipped by name composition). */
export const isOtherRef = (ref?: MachineRef | null): boolean =>
  !!ref && (ref.code === "other" || (!ref.code && ref.name === "Other"));

/**
 * Rows sorted by their localised label in the UI locale, `Other` last — the same order the server
 * gives in English (Name ascending, Other last). Use for type / operation / axis / automation lists
 * only: brand and taper labels do not change (and tapers keep the server's SortOrder).
 */
export const sortReferenceRows = <T extends MachineRef>(
  rows: T[],
  kind: MachineRefKind,
  translate: Translate = defaultTranslate,
  locale: string = i18next.language || "en"
): T[] => {
  const keyed = rows.map((row) => ({ row, label: getReferenceLabel(kind, row, translate), other: isOtherRef(row) }));
  keyed.sort((a, b) => {
    if (a.other !== b.other) return a.other ? 1 : -1;
    return a.label.localeCompare(b.label, locale);
  });
  return keyed.map((k) => k.row);
};
